/**
 * React Hook for GA4 Tracking
 *
 * Provides convenient access to GA4 tracking methods in React components.
 * Includes automatic page view tracking on route changes.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ga4, getPriceBand, getPostcodeSector, UserTier, PropertyParams, ViewSource } from '../services/ga4';

// ============================================================================
// Page View Tracking Hook
// ============================================================================

/**
 * Automatically track page views on route changes
 * Use this once in App.tsx or a top-level component
 */
export function usePageTracking() {
  const location = useLocation();
  const previousPath = useRef<string>('');

  useEffect(() => {
    // Avoid duplicate tracking for the same path
    if (location.pathname !== previousPath.current) {
      ga4.navPageView(location.pathname);
      previousPath.current = location.pathname;
    }
  }, [location.pathname]);
}

// ============================================================================
// Main GA4 Hook
// ============================================================================

/**
 * Main hook for GA4 tracking - provides all tracking methods
 */
export function useGA4() {
  // ==========================================================================
  // Authentication Tracking
  // ==========================================================================

  const trackLoginStart = useCallback((method: 'email' | 'google' | 'apple' = 'email') => {
    ga4.authLoginStart(method);
  }, []);

  const trackLoginSuccess = useCallback((method: 'email' | 'google' | 'apple' = 'email', userTier?: UserTier) => {
    ga4.authLoginSuccess(method, userTier);
  }, []);

  const trackLoginFailure = useCallback((method: 'email' | 'google' | 'apple' = 'email', errorType?: string) => {
    ga4.authLoginFailure(method, errorType);
  }, []);

  const trackLogout = useCallback(() => {
    ga4.authLogout();
    ga4.clearUser();
  }, []);

  const trackSignupStart = useCallback(() => {
    ga4.authSignupStart();
  }, []);

  const trackSignupComplete = useCallback((userTier: UserTier = 'STARTER') => {
    ga4.authSignupComplete(userTier);
  }, []);

  const trackSignupFailure = useCallback((errorType?: string) => {
    ga4.authSignupFailure(errorType);
  }, []);

  const trackPasswordReset = useCallback(() => {
    ga4.authPasswordReset();
  }, []);

  const trackEmailVerify = useCallback(() => {
    ga4.authEmailVerify();
  }, []);

  // ==========================================================================
  // Property Tracking
  // ==========================================================================

  const trackPropertyView = useCallback((params: PropertyParams & { source?: ViewSource }) => {
    ga4.propertyView(params);
  }, []);

  const trackPropertyUnlock = useCallback((params: PropertyParams & {
    credits_used?: number;
    credits_remaining?: number;
  }) => {
    ga4.propertyUnlock(params);
  }, []);

  const trackPropertyFavourite = useCallback((
    params: PropertyParams,
    action: 'add' | 'remove',
    source?: 'card' | 'detail' | 'map'
  ) => {
    if (action === 'add') {
      ga4.propertyFavouriteAdd({ ...params, source });
    } else {
      ga4.propertyFavouriteRemove({ ...params, source });
    }
  }, []);

  const trackPropertyShareStart = useCallback((propertyId: string) => {
    ga4.propertyShareStart(propertyId);
  }, []);

  const trackPropertyShareComplete = useCallback((propertyId: string, recipientIsMember: boolean) => {
    ga4.propertyShareComplete(propertyId, recipientIsMember);
  }, []);

  const trackPropertyPdfExport = useCallback((params: PropertyParams & {
    has_crime_data?: boolean;
    has_poi_data?: boolean;
    strategies_count?: number;
  }) => {
    ga4.propertyPdfExport(params);
  }, []);

  const trackPropertyImageView = useCallback((propertyId: string, imageIndex: number) => {
    ga4.propertyImageView(propertyId, imageIndex);
  }, []);

  // ==========================================================================
  // Filter Tracking
  // ==========================================================================

  const trackFilterApply = useCallback((filterName: string, filterCategory: string) => {
    ga4.filterApply(filterName, filterCategory);
  }, []);

  const trackFilterClearAll = useCallback(() => {
    ga4.filterClearAll();
  }, []);

  const trackFilterClearSingle = useCallback((filterName: string) => {
    ga4.filterClearSingle(filterName);
  }, []);

  const trackFilterPresetSave = useCallback((presetName: string) => {
    ga4.filterPresetSave(presetName);
  }, []);

  const trackFilterPresetLoad = useCallback((presetName: string) => {
    ga4.filterPresetLoad(presetName);
  }, []);

  const trackFilterPresetDelete = useCallback((presetName: string) => {
    ga4.filterPresetDelete(presetName);
  }, []);

  const trackFilterPanelToggle = useCallback((isOpen: boolean) => {
    if (isOpen) {
      ga4.filterPanelOpen();
    } else {
      ga4.filterPanelClose();
    }
  }, []);

  const trackFilterCategoryToggle = useCallback((category: string, isExpanded: boolean) => {
    if (isExpanded) {
      ga4.filterCategoryExpand(category);
    } else {
      ga4.filterCategoryCollapse(category);
    }
  }, []);

  // ==========================================================================
  // Navigation Tracking
  // ==========================================================================

  const trackSortChange = useCallback((sortOption: string, previousOption?: string, resultsCount?: number) => {
    ga4.navSortChange(sortOption, previousOption, resultsCount);
  }, []);

  const trackViewModeChange = useCallback((viewMode: 'list' | 'map', previousMode?: 'list' | 'map') => {
    ga4.navViewModeChange(viewMode, previousMode);
  }, []);

  const trackMapMarkerClick = useCallback((propertyId: string) => {
    ga4.navMapMarkerClick(propertyId);
  }, []);

  const trackMapZoom = useCallback((zoomLevel: number) => {
    ga4.navMapZoom(zoomLevel);
  }, []);

  const trackScrollLoadMore = useCallback((pageNumber: number, itemsLoaded: number) => {
    ga4.navScrollLoadMore(pageNumber, itemsLoaded);
  }, []);

  const trackExternalLink = useCallback((destination: 'external' | 'source' | 'other', propertyId?: string) => {
    ga4.navExternalLink(destination, propertyId);
  }, []);

  const trackTabChange = useCallback((tabName: string, previousTab?: string) => {
    ga4.navTabChange(tabName, previousTab);
  }, []);

  const trackSidebarToggle = useCallback((isOpen: boolean) => {
    ga4.navSidebarToggle(isOpen);
  }, []);

  // ==========================================================================
  // User Tracking
  // ==========================================================================

  const trackFeedbackSubmit = useCallback((feedbackType: 'feedback' | 'feature' | 'bug') => {
    ga4.userFeedbackSubmit(feedbackType);
  }, []);

  const trackPreferencesUpdate = useCallback((preferencesChanged: string[]) => {
    ga4.userPreferencesUpdate(preferencesChanged);
  }, []);

  const trackNotificationView = useCallback((notificationType: string) => {
    ga4.userNotificationView(notificationType);
  }, []);

  const trackNotificationClick = useCallback((notificationType: string, action: string) => {
    ga4.userNotificationClick(notificationType, action);
  }, []);

  const trackNotificationDismiss = useCallback((notificationType: string) => {
    ga4.userNotificationDismiss(notificationType);
  }, []);

  const trackReferralShare = useCallback((method: 'copy' | 'email' | 'social') => {
    ga4.userReferralShare(method);
  }, []);

  const trackTourStart = useCallback(() => {
    ga4.userTourStart();
  }, []);

  const trackTourComplete = useCallback(() => {
    ga4.userTourComplete();
  }, []);

  const trackTourSkip = useCallback((stepNumber: number) => {
    ga4.userTourSkip(stepNumber);
  }, []);

  // ==========================================================================
  // Billing Tracking
  // ==========================================================================

  const trackCheckoutStart = useCallback((itemType: 'subscription' | 'credits', itemName: string) => {
    ga4.billingCheckoutStart(itemType, itemName);
  }, []);

  const trackCheckoutComplete = useCallback((itemType: 'subscription' | 'credits', itemName: string, value: number) => {
    ga4.billingCheckoutComplete(itemType, itemName, value);
  }, []);

  const trackCheckoutAbandon = useCallback((itemType: 'subscription' | 'credits', itemName: string) => {
    ga4.billingCheckoutAbandon(itemType, itemName);
  }, []);

  const trackSubscriptionChange = useCallback((fromTier: UserTier, toTier: UserTier) => {
    ga4.billingSubscriptionChange(fromTier, toTier);
  }, []);

  const trackSubscriptionCancel = useCallback((tier: UserTier) => {
    ga4.billingSubscriptionCancel(tier);
  }, []);

  const trackCreditsPurchase = useCallback((packName: string, creditsAmount: number, value: number) => {
    ga4.billingCreditsPurchase(packName, creditsAmount, value);
  }, []);

  const trackCreditsLow = useCallback((creditsRemaining: number) => {
    ga4.billingCreditsLow(creditsRemaining);
  }, []);

  // ==========================================================================
  // Error Tracking
  // ==========================================================================

  const trackApiError = useCallback((errorCode: number, errorType: string, endpoint: string) => {
    ga4.errorApi(errorCode, errorType, endpoint);
  }, []);

  const trackValidationError = useCallback((fieldName: string, validationType: string) => {
    ga4.errorValidation(fieldName, validationType);
  }, []);

  const trackAuthError = useCallback((errorType: 'token_expired' | 'unauthorized' | 'session_invalid') => {
    ga4.errorAuth(errorType);
  }, []);

  const trackPaymentError = useCallback((errorType: string) => {
    ga4.errorPayment(errorType);
  }, []);

  const trackNetworkError = useCallback(() => {
    ga4.errorNetwork();
  }, []);

  // ==========================================================================
  // User Identity
  // ==========================================================================

  const setUser = useCallback((userId: string | null, properties?: { tier?: UserTier }) => {
    ga4.setUser(userId, properties);
  }, []);

  const setUserProperties = useCallback((properties: Record<string, any>) => {
    ga4.setUserProperties(properties);
  }, []);

  const clearUser = useCallback(() => {
    ga4.clearUser();
  }, []);

  // Return all tracking methods
  return {
    // Auth
    trackLoginStart,
    trackLoginSuccess,
    trackLoginFailure,
    trackLogout,
    trackSignupStart,
    trackSignupComplete,
    trackSignupFailure,
    trackPasswordReset,
    trackEmailVerify,

    // Property
    trackPropertyView,
    trackPropertyUnlock,
    trackPropertyFavourite,
    trackPropertyShareStart,
    trackPropertyShareComplete,
    trackPropertyPdfExport,
    trackPropertyImageView,

    // Filter
    trackFilterApply,
    trackFilterClearAll,
    trackFilterClearSingle,
    trackFilterPresetSave,
    trackFilterPresetLoad,
    trackFilterPresetDelete,
    trackFilterPanelToggle,
    trackFilterCategoryToggle,

    // Navigation
    trackSortChange,
    trackViewModeChange,
    trackMapMarkerClick,
    trackMapZoom,
    trackScrollLoadMore,
    trackExternalLink,
    trackTabChange,
    trackSidebarToggle,

    // User
    trackFeedbackSubmit,
    trackPreferencesUpdate,
    trackNotificationView,
    trackNotificationClick,
    trackNotificationDismiss,
    trackReferralShare,
    trackTourStart,
    trackTourComplete,
    trackTourSkip,

    // Billing
    trackCheckoutStart,
    trackCheckoutComplete,
    trackCheckoutAbandon,
    trackSubscriptionChange,
    trackSubscriptionCancel,
    trackCreditsPurchase,
    trackCreditsLow,

    // Errors
    trackApiError,
    trackValidationError,
    trackAuthError,
    trackPaymentError,
    trackNetworkError,

    // User identity
    setUser,
    setUserProperties,
    clearUser,

    // Helpers (re-exported for convenience)
    getPriceBand,
    getPostcodeSector,
  };
}

// Export default for convenience
export default useGA4;
